import React, { Component, useEffect, useMemo } from "react";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import { useQuery } from "react-apollo";
import { ToastContainer } from "react-toastify";
import { IntlProvider } from "react-intl";
import { useIntl } from "react-intl";
import ReactDOM from "react-dom";
import _ from "lodash";
import { apolloClient, serverStackClient, AppContext, withContext, QUERY_ME } from "./context";

import "./App.scss";
import "./styles/tailwind.css"; // must stay after App.scss
import { getTranslation, getCookie, setCookie } from "./utils";
import { getAccessToken, clearAccessToken } from "./utils/token";
import { clearAttempted, clearLoginBounces } from "./utils/silentAuth";
import GenericModal from "./components/Modal/GenericModal";
import ApolloProvider from "./apollo-client/apollo-provider";
import { serverStackClientV2 } from "./apollo-client/client";
import { useAuth } from "./components/auth";

const IncomingCalls = React.lazy(() => import("./components/Voice/IncomingCalls"));

const Loading = () => <div className="animated fadeIn pt-3 text-center">Loading...</div>;

// Containers
const DefaultLayout = React.lazy(() => import("./containers/DefaultLayout"));

// Pages
// /login no longer collects credentials — see utils/silentAuth.js. The path is
// unchanged so existing redirects to /login keep working.
const SilentAuthPage = React.lazy(() => import("./views/Pages/SilentAuth/SilentAuthPage"));
// Không cần login, không có layout — đích của link "ask seller availability" trong mail
const CarAvailabilityConfirmPage = React.lazy(() => import("./views/Pages/CarAvailabilityConfirm/CarAvailabilityConfirmPage"));
const Page404 = React.lazy(() => import("./views/Pages/Page404"));
const Page500 = React.lazy(() => import("./views/Pages/Page500"));

const isValidSeller = (isAgent, isAdmin, me) => {
  const { companies = [] } = me;
  let isBlock = false;
  if (!isAdmin && _.isEmpty(companies)) return true;
  for (let company of companies) {
    if (["pending", "suspended"].includes(company.status) || !company.inContract) {
      isBlock = true;
    }
  }
  return isBlock && !isAgent;
};

export const AppContextProvider = ({ switchLang, locale, children }) => {
  const { data, loading } = useQuery(QUERY_ME, { client: serverStackClient });
  const { renewTokenIfNeeded } = useAuth();
  // `_companies` là nullable trên V3, và default `= []` khi destructure chỉ áp
  // cho `undefined` chứ không cho `null` — chuẩn hoá 1 lần ở đây để không
  // consumer nào phải tự guard (isValidSeller iterate thẳng vào nó).
  const raw = data?.context?.me;
  const me = useMemo(() => raw && { ...raw, companies: raw.companies || [] }, [raw]);

  // Renew the JWT on startup when it has < 7 days of life left (session → JWT).
  useEffect(() => {
    renewTokenIfNeeded();
  }, [renewTokenIfNeeded]);

  // Purge a token the API will not accept. /login explains the refusal from the
  // handshake record in sessionStorage, so nothing needs the cookie kept.
  useEffect(() => {
    if (loading || me || !getAccessToken()) return;
    clearAccessToken();
  }, [loading, me]);

  // A loaded `me` means the last handshake got somewhere, so the record of which
  // account was refused goes — the next refusal should be judged on its own.
  // The attempt ceiling deliberately stays: a loop that renders an authenticated
  // page each time round (a token `me` accepts and the rest of the API refuses,
  // a forced logout from an error link) would reset it here every cycle and the
  // breaker could never trip. It ages out on its own instead.
  useEffect(() => {
    if (!me) return;
    clearAttempted();
    clearLoginBounces();
  }, [me]);

  if (loading) return <Loading />;

  const isAdmin = _.includes(me?.roles, "admin");
  const isAgent = isAdmin || _.includes(me?.roles, "agent");
  const isEnterprise = !!_.find(me?.companies, o => o.plan === "enterprise");

  return (
    <AppContext.Provider
      value={{
        me,
        isAdmin,
        isAgent,
        isEnterprise,
        switchLang,
        locale
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const Admin = withContext(props => {
  const { isAgent, isAdmin, me, location } = props;
  if (!props.me) {
    // Encoded: a deep link with its own query string ("/billing?invoice=1&page=2")
    // would otherwise have everything after the first & parsed as a sibling param
    // of `redirect` and dropped.
    return <Redirect to={"/login?redirect=" + encodeURIComponent(location.pathname + location.search)} />;
  }
  if (isValidSeller(isAgent, isAdmin, me)) return <WarningInvalidSeller />;
  return <DefaultLayout {...props} />;
});

const WarningInvalidSeller = () => {
  const intl = useIntl();
  return ReactDOM.createPortal(
    <GenericModal open title="Note">
      <div className="mt-2 gray-color">{intl.formatMessage({ id: "messageBlockSeller" })}</div>
      <div className="mt-2 gray-color">Tel: +81 50 5578 0494</div>
      <div className="mt-2 gray-color">
        E-mail: <strong className="strong-color">ask@carfromjapan.com</strong>
      </div>
    </GenericModal>,
    document.getElementsByTagName("body")[0]
  );
};

class App extends Component {
  state = {
    locale: getCookie("language") || "en"
  };
  switchLang = locale => {
    setCookie("language", locale);
    this.setState({ locale });
  };
  render() {
    const { locale } = this.state;
    const fileTranslation = getTranslation({ locale });
    return (
      <IntlProvider locale={locale} messages={fileTranslation} onError={() => {}}>
        <ApolloProvider clientV1={apolloClient} clientV2={serverStackClientV2}>
          <ToastContainer />
          <AppContextProvider switchLang={this.switchLang} locale={locale}>
            <BrowserRouter>
              <React.Suspense fallback={null}>
                <IncomingCalls />
              </React.Suspense>
              <React.Suspense fallback={<Loading />}>
                <Switch>
                  <Route exact path="/login" name="Login Page" render={props => <SilentAuthPage {...props} />} />
                  <Route exact path="/car-availability-confirm" name="Car Availability Confirm" render={props => <CarAvailabilityConfirmPage {...props} />} />
                  <Route exact path="/404" name="Page 404" render={props => <Page404 {...props} />} />
                  <Route exact path="/500" name="Page 500" render={props => <Page500 {...props} />} />
                  <Route path="/" name="Home" render={Admin} />
                </Switch>
              </React.Suspense>
            </BrowserRouter>
          </AppContextProvider>
        </ApolloProvider>
      </IntlProvider>
    );
  }
}

export default App;
